import { Ban, BookMarked, CheckCircle2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { BudgetAccountCodeModal } from '../components/BudgetAccountCodeModal';
import { budgetAccountCodesApi } from '../budgetCatalogsApi';
import type { BudgetAccountCodeListItem } from '../budgetCatalogTypes';

type TabKey = 'info';

/** One expenditure account in the chart of accounts. */
export function BudgetAccountCodeProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [account, setAccount] = useState<BudgetAccountCodeListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      budgetAccountCodesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setAccount(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this account.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await budgetAccountCodesApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this account.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Settings', to: '/procurement/investment-planning/settings' },
    { label: 'Account Codes', to: '/procurement/investment-planning/settings/account-codes' },
    account?.code ?? 'Account code',
  ];

  if (error && !account) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !account) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="budget-account-code-profile"
        header={
          <ProfileHeader
            id="budget-account-code-profile-header"
            icon={<BookMarked className="h-6 w-6" aria-hidden="true" />}
            /*
              The CODE is the title, because the code is the identity here — it is what a budget
              line is charged against and what the chart of accounts is read in order of. The
              description is the meta beside it, and the two are never the same string.
            */
            title={account.code}
            meta={[
              ...(account.description ? [account.description] : []),
              ...(account.acctNo ? [`Account no. ${account.acctNo}`] : []),
            ]}
            status={
              <StatusText tone={account.isActive ? 'active' : 'inactive'}>
                {account.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={account.createdByName}
            createdAt={account.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="budget-account-code-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : account.isActive ? (
            <ProfileBanner
              id="budget-account-code-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This account is offered: it appears in the Account code picker when a budget line item is charged."
            />
          ) : (
            <ProfileBanner
              id="budget-account-code-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This account is deleted. It is no longer offered, and the budget lines already charged to it keep resolving it."
              action={
                <PermissionGate permission="budget-account-codes.delete">
                  <Button
                    id="budget-account-code-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="budget-account-code-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'budget-account-codes.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Code', value: account.code, mono: true },
              { label: 'Account no.', value: account.acctNo, mono: true },
              { label: 'Description', value: account.description },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="budget-account-code-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: account.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(account.createdAt)} className="font-mono">
                    {formatDateTime(account.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: account.updatedByName },
              {
                label: 'Updated',
                value: account.updatedAt ? (
                  <time dateTime={toIso(account.updatedAt)} className="font-mono">
                    {formatDateTime(account.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <BudgetAccountCodeModal
          open
          editing={account}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
